<script setup>
import ItemThumbnail from "@/Components/ItemThumbnail.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import MiniPreview from "@/Components/MiniPreview.vue";
import { getEquipmentImageUrl, shouldUsePlaceholder } from "@/Helpers/EquipmentPlaceholderHelper.js";
import { getCategoryLabel, getPropertyConfig, getPrimaryProperty } from "@/Helpers/EquipmentPropertiesHelper.js";
import { computed } from "vue";
import EquipmentSkeletonRow from "@/Components/Equipment/EquipmentSkeletonRow.vue";

const emits = defineEmits(['close', 'updated', 'image-selected', 'item-selected']);

const props = defineProps({
    equipment: {
        type: Object,
        default: null,
        nullable: true,
    },
    stashId: {
        type: String,
        default: null,
        nullable: true,
    },
    pendingCount: {
        type: Number,
        default: 0,
    },
});


function descriptionTruncated(description) {
    if (!description) return '';
    if (description.length > 120) {
        return description.substring(0, 120) + "...";
    }
    return description;
}

</script>

<style scoped>
</style>

<template>
    <div class="overflow-hidden bg-gray-800 shadow-lg rounded-xl border border-gray-700">
        <ul role="list" class="divide-y divide-gray-700">
            <li v-for="item in props.equipment" :key="item.id">
                <div class="block hover:bg-gradient-to-r hover:from-gray-750 hover:to-gray-700 transition-all duration-300 cursor-pointer hover:shadow-md" @click="$emit('item-selected', item)">
                    <div class="p-5 sm:p-6">
                        <div class="flex items-start gap-4">
                            <!-- Equipment Image -->
                            <div class="flex-shrink-0" @click.stop="!shouldUsePlaceholder(item) ? $emit('image-selected', { imageUrl: getEquipmentImageUrl(item), item: item }) : $emit('item-selected', item)">
                                <ItemThumbnail
                                    v-if="item.id"
                                    :image-url="getEquipmentImageUrl(item)"
                                    :image-description="item.image_description"
                                    :immediate-load="shouldUsePlaceholder(item)"
                                    :width="'8rem'"
                                    :height="'8rem'"
                                    class="rounded-lg shadow-md"
                                />
                            </div>

                            <!-- Equipment Content -->
                            <div class="flex-1 min-w-0">
                                <!-- Header Row -->
                                <div class="flex items-start justify-between ">
                                    <div class="flex-1 min-w-0">
                                        <h3 class="text-lg font-semibold text-white truncate flex items-center">
                                            <div class="w-6 flex justify-center">
                                                <FontAwesomeIcon :icon="['fas', 'tag']" class="text-blue-400" size="lg" />
                                            </div>
                                            <span class="ml-3">{{ item.name }}</span>
                                        </h3>
                                        <div v-if="item.category" class="flex items-center mt-2">
                                            <div class="w-6 flex justify-center">
                                                <FontAwesomeIcon :icon="[getPropertyConfig(item, 'category')?.iconSet || 'fas', 'folder']" class="text-orange-400" size="sm" />
                                            </div>
                                            <span class="text-sm text-gray-300 ml-3">{{ getCategoryLabel(item.category) }}</span>
                                        </div>
                                    </div>
                                    <div class="ml-4 flex-shrink-0 hidden sm:block">
                                        <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-blue-400" />
                                    </div>
                                </div>

                                <!-- Properties Grid -->
                                <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4 mb-0">
                                    <!-- Primary Property (Damage/Armor) -->
                                    <div v-if="getPrimaryProperty(item)" class="flex items-center bg-gray-800/50 rounded-lg">
                                        <div class="w-6 flex justify-center">
                                            <FontAwesomeIcon
                                                :icon="[getPrimaryProperty(item).config.iconSet, getPrimaryProperty(item).config.icon]"
                                                :class="getPrimaryProperty(item).config.color"
                                                size="sm"
                                            />
                                        </div>
                                        <span class="text-sm text-gray-300 ml-3">{{ getPrimaryProperty(item).config.label }}:</span>
                                        <span class="text-sm font-medium text-white ml-2">{{ getPrimaryProperty(item).value }}</span>
                                    </div>

                                    <!-- Cost -->
                                    <div v-if="item.cost" class="flex items-center bg-gray-800/50 rounded-lg py-2">
                                        <div class="w-6 flex justify-center">
                                            <FontAwesomeIcon :icon="[getPropertyConfig(item, 'cost')?.iconSet || 'fas', 'coins']" class="text-yellow-400" size="sm" />
                                        </div>
                                        <span class="text-sm text-gray-300 ml-3">Cost:</span>
                                        <span class="text-sm font-medium text-white ml-2">{{ item.cost }}</span>
                                    </div>

                                    <!-- Weight -->
                                    <div v-if="item.weight" class="flex items-center bg-gray-800/50 rounded-lg  py-2">
                                        <div class="w-6 flex justify-center">
                                            <FontAwesomeIcon :icon="[getPropertyConfig(item, 'weight')?.iconSet || 'fas', 'weight']" class="text-green-400" size="sm" />
                                        </div>
                                        <span class="text-sm text-gray-300 ml-3">Weight:</span>
                                        <span class="text-sm font-medium text-white ml-2">{{ item.weight }}</span>
                                    </div>
                                </div>

                                <!-- Description -->
                                <div v-if="item.description" class="bg-gray-800/30 rounded-lg ">
                                    <div class="flex items-start">
                                        <div class="w-6 flex justify-center mt-1 ml-2 md:ml-0 lg:ml-0.5">
                                            <FontAwesomeIcon :icon="['fas', 'align-left']" class="text-cyan-400" size="sm" />
                                        </div>
                                        <p class="text-sm text-gray-300 leading-relaxed italic ml-3 sm:ml-2">{{ descriptionTruncated(item.description) }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Mobile Chevron -->
                            <div class="flex-shrink-0 sm:hidden">
                                <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-blue-400" />
                            </div>
                        </div>
                    </div>
                </div>
            </li>
            <EquipmentSkeletonRow v-if="props.pendingCount > 0" :count="props.pendingCount" />
        </ul>
    </div>
</template>
